import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabaseClient';
import { logActivity } from '../utils/activityLogger';
import { calculateDepreciation } from '../utils/assetUtils';
import { formatCurrency } from '../utils/formatters';
import Icon from './AppIcon';
import Button from './ui/Button';
import Select from './ui/Select';
import { useSelector } from 'react-redux';

const WriteOffModal = ({ asset, isOpen, onClose, onSuccess }) => {
    const [reason, setReason] = useState('');
    const [disposalMethod, setDisposalMethod] = useState('');
    const [justification, setJustification] = useState('');
    const [disposedBy, setDisposedBy] = useState('');
    const [disposedByContact, setDisposedByContact] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const { user: authUser } = useSelector((state) => state.auth);
    const userId = authUser?.id;

    if (!isOpen || !asset) return null;

    const currentValue = calculateDepreciation(
        asset.purchase_date,
        asset.purchase_price,
        asset.lifespan_months || (asset.lifespan_years ? asset.lifespan_years * 12 : 36)
    );

    const reasons = [
        { value: 'Beyond Economic Repair', label: 'Beyond Economic Repair' },
        { value: 'Obsolete', label: 'Obsolete' },
        { value: 'Lost/Stolen', label: 'Lost/Stolen' },
        { value: 'Destroyed', label: 'Destroyed' }
    ];

    const disposalMethods = [
        { value: 'E-Waste Recycling', label: 'E-Waste Recycling' },
        { value: 'Donated', label: 'Donated' },
        { value: 'Scrapped', label: 'Scrapped' },
        { value: 'Returned to Vendor', label: 'Returned to Vendor' }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!justification.trim()) {
            setError('Justification is required.');
            return;
        }
        if (!reason || !disposalMethod) {
            setError('Please select a reason and disposal method.');
            return;
        }
        if (!disposedBy.trim()) {
            setError('Please enter the name of the company or person taking the asset away.');
            return;
        }
        if (!disposedByContact.trim()) {
            setError('Please enter contact details for the company or person taking the asset away.');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            // 1. Update Asset Status and Archive Flag
            const { error: updateError } = await supabase
                .from('assets')
                .update({
                    status: 'Written Off',
                    is_archived: true
                })
                .eq('asset_tag', asset.asset_tag);

            if (updateError) throw updateError;

            // 2. Log Activity
            await logActivity(
                'asset_written_off',
                `Asset written off due to: ${reason}`,
                asset.asset_tag,
                userId,
                {
                    reason,
                    disposal_method: disposalMethod,
                    justification,
                    disposed_by: disposedBy,
                    disposed_by_contact: disposedByContact,
                    original_price: asset.purchase_price,
                    estimated_value_at_write_off: currentValue,
                    authorized_by: authUser?.email || userId
                }
            );

            onSuccess?.(`Asset ${asset.asset_tag} has been successfully written off.`);
            onClose();
        } catch (err) {
            console.error('Error writing off asset:', err);
            setError(err.message || 'Failed to write off asset. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal Content */}
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-card border border-border rounded-xl shadow-2xl max-w-lg w-full relative z-10 overflow-hidden max-h-[90vh] flex flex-col"
            >
                {/* Header */}
                <div className="p-6 border-b border-border bg-error/5 flex-shrink-0">
                    <div className="flex items-center gap-3 text-error">
                        <div className="p-2 bg-error/10 rounded-lg">
                            <Icon name="AlertTriangle" size={24} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold">Confirm Asset Write-Off</h3>
                            <p className="text-sm text-error/80">This action is permanent and removes the asset from active circulation.</p>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto">
                    {/* Financial Context Block */}
                    <div className="bg-muted/50 rounded-lg p-4 border border-border grid grid-cols-2 gap-4">
                        <div>
                            <span className="text-xs text-muted-foreground block uppercase font-semibold">Original Price</span>
                            <span className="text-lg font-bold text-foreground">{formatCurrency(asset.purchase_price)}</span>
                        </div>
                        <div>
                            <span className="text-xs text-muted-foreground block uppercase font-semibold">Estimated Current Value</span>
                            <span className="text-lg font-bold text-foreground">{formatCurrency(currentValue)}</span>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <Select
                            label="Reason for Write-Off"
                            options={reasons}
                            value={reason}
                            onChange={setReason}
                            placeholder="Select a reason"
                            required
                        />

                        <Select
                            label="Disposal Method"
                            options={disposalMethods}
                            value={disposalMethod}
                            onChange={setDisposalMethod}
                            placeholder="Select disposal method"
                            required
                        />

                        {/* Handled By / Disposal Contractor Section */}
                        <div className="rounded-lg border border-border p-4 space-y-3 bg-muted/30">
                            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                <Icon name="Truck" size={15} className="text-primary" />
                                Disposal Contractor Details
                                <span className="text-error text-xs font-bold ml-1">* Required</span>
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-foreground">
                                    Disposed By / Company Name <span className="text-error">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="w-full p-2.5 rounded-md border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                    placeholder="Company or representative taking the asset (e.g. Recyco Corp)"
                                    value={disposedBy}
                                    onChange={(e) => setDisposedBy(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-foreground">
                                    Contractor Contact (Email / Phone) <span className="text-error">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="w-full p-2.5 rounded-md border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                    placeholder="e.g. contact@recyco.com or +60 12-345 6789"
                                    value={disposedByContact}
                                    onChange={(e) => setDisposedByContact(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">
                                Justification / Audit Notes <span className="text-error">*</span>
                            </label>
                            <textarea
                                className="w-full min-h-[90px] p-3 rounded-md border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                placeholder="Provide detailed justification for auditing purposes..."
                                value={justification}
                                onChange={(e) => setJustification(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="p-3 bg-error/10 border border-error/20 rounded-md flex items-center gap-2 text-error text-sm">
                            <Icon name="AlertCircle" size={16} />
                            {error}
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4 border-t border-border">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="bg-error hover:bg-error/90 text-white border-none"
                            iconName="Trash2"
                            loading={isSubmitting}
                        >
                            Confirm Write-Off
                        </Button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

export default WriteOffModal;
